/**
 * Cristaux drawer checkout
 * ---------------------------------------------------------------------------
 * The Cristaux app runs its checkout logic only on /cart (scripts.js:2089–2224).
 * The old theme forced every order through /cart; the new drawer checks out
 * directly, so this script does the same work on the drawer's Checkout button:
 *
 *   1. Validates the optional Event Date against the vendor lead time
 *   2. Waits for the cart sync (setup fee + color fill, assets/cristaux-setup-fee.js)
 *   3. Blocks checkout if color fills outnumber products (same alert as the app)
 *   4. Fetches the order Unique Id from the app (links app-side data to the order)
 *   5. Submits the drawer form, so Shopify saves the "Event Date" and "Unique Id"
 *      cart attributes and redirects to checkout
 *
 * Fixes vs. the app: dates are read in the customer's local time (the app parsed
 * them as UTC, which rejected the earliest valid date in US time zones), and the
 * error message states the real lead time instead of always "10 days".
 *
 * If anything unexpected fails (network, app down), checkout still goes ahead:
 * only an invalid Event Date or excess color fills stop it, as in the app.
 *
 * Markup: snippets/cristaux-event-date.liquid. Config: window.CristauxCheckoutConfig
 * (snippets/cristaux-app.liquid).
 */
(() => {
  const cfg = window.CristauxCheckoutConfig || {};
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const COLOR_FILL_SKU = 'COLORFILL';
  const LATEST_EXTRA_DAYS = 90;
  const UNIQUE_ID_TIMEOUT_MS = 5000;

  const pad = (n) => String(n).padStart(2, '0');
  const toIso = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const fromIso = (value) => {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  };
  const today = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  };
  const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
  const formatDate = (date) =>
    date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const saveAttributes = (attributes) =>
    fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });

  class CristauxEventDate extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="date"]');
      this.error = this.querySelector('.cristaux-event-date__error');
      if (!this.input) return;

      const leadDays = parseInt(this.dataset.leadDays, 10) || 10;
      this.leadDays = leadDays;
      this.earliest = addDays(today(), leadDays);
      this.latest = addDays(this.earliest, LATEST_EXTRA_DAYS);
      this.input.min = toIso(this.earliest);
      this.input.max = toIso(this.latest);
      const earliestLabel = this.querySelector('[data-earliest]');
      if (earliestLabel) earliestLabel.textContent = formatDate(this.earliest);

      // A saved date can become invalid when a longer-lead item is added.
      if (this.input.value) this.validate();

      this.input.addEventListener('change', () => {
        // Save as you go, so the date survives drawer re-renders. Invalid dates
        // are not saved; checkout re-validates anyway.
        if (this.validate()) {
          saveAttributes({ 'Event Date': this.input.value }).catch((error) =>
            console.error('[Cristaux checkout] could not save event date:', error)
          );
        }
      });
    }

    get value() {
      return this.input ? this.input.value : '';
    }

    message() {
      if (!this.value) return '';
      const date = fromIso(this.value);
      if (Number.isNaN(date.getTime())) return 'Please enter a valid event date.';
      if (date < today()) return 'Please enter the current or a future date.';
      if (date < this.earliest) {
        return `Please choose a date at least ${this.leadDays} days from today (${formatDate(this.earliest)} or later).`;
      }
      if (date > this.latest) {
        return `The date you selected is too far ahead. Please choose ${formatDate(this.latest)} or earlier.`;
      }
      return '';
    }

    validate() {
      const message = this.message();
      if (this.error) {
        this.error.textContent = message;
        this.error.hidden = !message;
      }
      this.input.setAttribute('aria-invalid', message ? 'true' : 'false');
      return !message;
    }
  }

  if (!customElements.get('cristaux-event-date')) {
    customElements.define('cristaux-event-date', CristauxEventDate);
  }

  const fetchCart = () =>
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

  // Same check as the app's validateQuantity (scripts.js:652), counted correctly:
  // total color fills vs. total regular products.
  const colorFillsExceedProducts = (cart) => {
    const excluded = new Set(((window.CristauxFeeConfig || {}).excludedVariantIds || []).map(String));
    let fills = 0;
    let products = 0;
    cart.items.forEach((item) => {
      if (item.sku === COLOR_FILL_SKU) fills += item.quantity;
      else if (!excluded.has(String(item.variant_id))) products += item.quantity;
    });
    return fills > products;
  };

  // Same endpoint and token as the app (scripts.js:100). Never blocks checkout.
  const fetchUniqueId = async (token) => {
    if (!cfg.appDomain || !token) return '';
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), UNIQUE_ID_TIMEOUT_MS);
    try {
      const response = await fetch(`${cfg.appDomain}/api/shopify/services/orders/get-order-id/${token}`, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      const data = await response.json();
      return data && data.order_id ? String(data.order_id) : '';
    } catch (error) {
      console.warn('[Cristaux checkout] could not get order Unique Id:', error);
      return '';
    } finally {
      clearTimeout(timer);
    }
  };

  // Save the Unique Id onto the cart as soon as it has items, so it is there
  // even if something else (another app) sends the customer to checkout without
  // going through the submit handler below. One app call per cart token.
  const attemptedTokens = new Set();
  let ensuring = null;
  const ensureUniqueId = () => {
    if (ensuring) return ensuring;
    ensuring = (async () => {
      const cart = await fetchCart();
      const saved = (cart.attributes || {})['Unique Id'];
      if (!cart.item_count || saved || attemptedTokens.has(cart.token)) return saved || '';
      attemptedTokens.add(cart.token);
      const uniqueId = await fetchUniqueId(cart.token);
      if (uniqueId) {
        await saveAttributes({ 'Unique Id': uniqueId });
        const input = document.getElementById('CartDrawer-UniqueId');
        if (input) input.value = uniqueId;
      }
      return uniqueId;
    })()
      .catch((error) => {
        console.warn('[Cristaux checkout] could not save order Unique Id:', error);
        return '';
      })
      .finally(() => {
        ensuring = null;
      });
    return ensuring;
  };

  const startUniqueIdSync = () => {
    if (window.CristauxFeeConfig && window.CristauxFeeConfig.isCartPage) return; // the app handles /cart
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(ensureUniqueId, 0));
    }
    ensureUniqueId();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startUniqueIdSync);
  } else {
    startUniqueIdSync();
  }

  const setBusy = (button, busy) => {
    if (!button) return;
    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
  };

  let checkingOut = false;

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'CartDrawer-Form') return;
    // Only intercept the Checkout button; other submits behave as before.
    if (event.submitter && event.submitter.name !== 'checkout') return;

    event.preventDefault();
    if (checkingOut) return;
    checkingOut = true;
    const button = event.submitter || document.getElementById('CartDrawer-Checkout');
    setBusy(button, true);

    let proceed = true;
    try {
      const picker = document.querySelector('cristaux-event-date');
      if (picker && typeof picker.validate === 'function' && !picker.validate()) {
        picker.input.focus();
        proceed = false;
        return;
      }

      if (window.CristauxCartSync) await window.CristauxCartSync.run();

      const cart = await fetchCart();
      if (colorFillsExceedProducts(cart)) {
        alert('Warning! It is not possible to increase the number of color fills beyond the number of products!');
        proceed = false;
        return;
      }

      const uniqueId = (cart.attributes || {})['Unique Id'] || (await fetchUniqueId(cart.token));
      const uniqueIdInput = document.getElementById('CartDrawer-UniqueId');
      if (uniqueId && uniqueIdInput) uniqueIdInput.value = uniqueId;

      // Save both attributes on the cart itself before leaving, as well as
      // sending them with the form, so checkout always sees them.
      const attributes = {};
      if (picker && picker.value) attributes['Event Date'] = picker.value;
      if (uniqueId) attributes['Unique Id'] = uniqueId;
      if (Object.keys(attributes).length) await saveAttributes(attributes);
    } catch (error) {
      // Unexpected failure: don't trap the customer, go to checkout anyway.
      console.error('[Cristaux checkout] pre-checkout step failed:', error);
    } finally {
      // The cart sync may have re-rendered the drawer: use the live elements.
      const liveForm = document.getElementById('CartDrawer-Form') || form;
      if (proceed) {
        // form.submit() skips the submit event (no loop) and the submitter,
        // so add the checkout flag the button would have sent.
        const flag = document.createElement('input');
        flag.type = 'hidden';
        flag.name = 'checkout';
        flag.value = 'Checkout';
        flag.dataset.cristauxCheckoutFlag = '';
        liveForm.appendChild(flag);
        setBusy(document.getElementById('CartDrawer-Checkout'), true);
        liveForm.submit();
      } else {
        setBusy(document.getElementById('CartDrawer-Checkout') || button, false);
        checkingOut = false;
      }
    }
  });

  // Back button from checkout restores this page from cache: re-enable checkout.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    checkingOut = false;
    document.querySelectorAll('[data-cristaux-checkout-flag]').forEach((flag) => flag.remove());
    setBusy(document.getElementById('CartDrawer-Checkout'), false);
  });
})();
